import { AdditionalInformationValueType } from '@bpos';
import { BetType } from '@bpos/common';
import { BetDetailType, PriceType as BposPriceType } from '@bpos/common/bet-placement';
import { Bet, Pick } from '@bpos/v2/bet-placement';
import { BposOddsConverterService } from '@frontend/sports/odds/feature';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';

import { IBetslipMessages } from '../../../../../base/models/betslip-messages';
import { IBetslipState } from '../../../../../base/store/state';
import { BetslipV2OptionMarketPick } from '../../../../../core/picks/betslip-v2-option-market-pick';
import { IBoostedPrice, ParticipantPickType, PriceType } from '../../../../../core/picks/pick-models';
import {
    BetslipV2HorseRaceOptionMarketPick,
    BetslipV2OptionMarketXCastRacePick,
} from '../../../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { ISlipPick } from '../../../../bet-generation/models';
import { BasePickRequestMapper } from './base-pick-request-mapper';
import { EmptyName, EmptyStringWithSignature, mapTeaserBetOptionName, mapTextValueData } from './text-value-helper';

export class V2OptionMarketPickRequestMapper extends BasePickRequestMapper {
    constructor(
        bposOddsConverterService: BposOddsConverterService,
        userOddsFormatSettingsService: UserOddsFormatSettingsService,
        private messages: IBetslipMessages,
    ) {
        super(bposOddsConverterService, userOddsFormatSettingsService);
    }

    mapBetDetails(
        slipPick: ISlipPick<BetslipV2OptionMarketPick | BetslipV2OptionMarketXCastRacePick>,
        bet: Bet,
        boostedPriceDetails?: IBoostedPrice,
        _state?: IBetslipState,
        options?: { isTeaser?: boolean; isInGroup?: boolean },
    ): void {
        const pick = slipPick.pick;
        if (pick instanceof BetslipV2HorseRaceOptionMarketPick || pick instanceof BetslipV2OptionMarketXCastRacePick) {
            bet.picks = this.generatePicks(slipPick);

            const betType = this.getBetType(slipPick);

            if (betType) {
                const betTypeName = this.getBetName(slipPick, betType);
                bet.betTypeDetails = {
                    betType,
                    name: pick instanceof BetslipV2OptionMarketXCastRacePick ? pick.betType : betTypeName,
                    altName: pick.betTypeAltTranslation,
                };
            }
        } else {
            bet.picks = [
                {
                    id:
                        !options?.isInGroup && boostedPriceDetails?.isPriceBoosted
                            ? boostedPriceDetails.boostedPriceId
                            : slipPick.priceIds[0].toString(10),
                },
            ] as Pick[];
            if (pick.isEachWay) {
                bet.betTypeDetails = {
                    betType: slipPick.eachWayInfo ? BetType.EachWay : BetType.Win,
                    name: this.messages.betPlacementRequest.BetTypeEachWay,
                    altName: this.messages.racingBetTypes.betTypeWin,
                };
            }
        }
        // Sport
        bet.betDetails.push({
            id: pick.sportId.toString(10),
            betDetailType: BetDetailType.Sport,
            additionalInformation: { informationItems: [] },
            ...mapTextValueData(pick, 'sportName'),
        });

        // Region
        bet.betDetails.push({
            id: pick.regionId.toString(10),
            betDetailType: BetDetailType.Region,
            additionalInformation: { informationItems: [] },
            ...mapTextValueData(pick, 'regionName'),
        });

        // League
        bet.betDetails.push({
            id: pick.leagueId.toString(10),
            betDetailType:
                pick instanceof BetslipV2HorseRaceOptionMarketPick || pick instanceof BetslipV2OptionMarketXCastRacePick
                    ? BetDetailType.Meeting
                    : BetDetailType.Competition,
            additionalInformation: { informationItems: [] },
            ...mapTextValueData(pick, 'leagueName'),
        });

        // Fixture
        bet.betDetails.push({
            id: pick.fixture.fixtureId,
            betDetailType: BetDetailType.Fixture,
            additionalInformation: { informationItems: [] },
            ...mapTextValueData(pick, 'eventName'),
        });

        // Market
        bet.betDetails.push({
            id: pick.market.id.toString(10),
            betDetailType: BetDetailType.Market,
            additionalInformation: { informationItems: [] },
            ...mapTextValueData(pick, 'marketName'),
        });

        // Option
        if (pick instanceof BetslipV2OptionMarketXCastRacePick) {
            pick.options.forEach((option) => {
                bet.betDetails.push({
                    id: option.id.toString(10),
                    betDetailType: BetDetailType.Option,
                    additionalInformation: { informationItems: [] },
                    ...mapTextValueData(option, 'name'),
                });
            });
        } else {
            const textValue = {
                value: options?.isTeaser ? mapTeaserBetOptionName(pick, pick.optionName.name, _state) : pick.optionName.name,
                sign: pick.optionName.signature,
            };
            let altTextValue = EmptyStringWithSignature;
            if (pick.optionName.nameAlternate && pick.optionName.signatureAlternate) {
                altTextValue = {
                    value: options?.isTeaser ? mapTeaserBetOptionName(pick, pick.optionName.nameAlternate, _state) : pick.optionName.nameAlternate,
                    sign: pick.optionName.signatureAlternate,
                };
            }

            bet.betDetails.push({
                id: pick.option.id.toString(10),
                betDetailType: BetDetailType.Option,
                additionalInformation: { informationItems: [] },
                textValue,
                altTextValue,
            });
        }

        bet.additionalInformation.informationItems.push({
            key: 'fixtureStartTime',
            value: pick.fixture.eventDate.toISOString(),
            valueType: AdditionalInformationValueType.DateTime,
        });

        bet.additionalInformation.informationItems.push({
            key: 'fixtureType',
            value: pick.fixture.fixtureType,
            valueType: AdditionalInformationValueType.Enum,
        });
    }

    private getBetName(slipPick: ISlipPick<BetslipV2OptionMarketPick | BetslipV2OptionMarketXCastRacePick>, betType: BetType): string {
        const pick = slipPick.pick;

        if (BetslipV2HorseRaceOptionMarketPick.isPick(pick)) {
            if (pick.isEachWay && slipPick.eachWayInfo) {
                return this.messages.betPlacementRequest.BetTypeEachWay;
            }
        }

        return this.messages.betPlacementRequest[('BetType' + betType) as keyof typeof this.messages.betPlacementRequest];
    }

    private getBetType(slipPick: ISlipPick<BetslipV2OptionMarketPick | BetslipV2OptionMarketXCastRacePick>): BetType | undefined {
        const pick = slipPick.pick;

        if (pick instanceof BetslipV2OptionMarketPick) {
            return pick.isEachWay && slipPick.eachWayInfo ? BetType.EachWay : BetType.Win;
        } else {
            switch (pick.id.pickType) {
                case ParticipantPickType.Forecast:
                    return BetType.Forecast;
                case ParticipantPickType.CombinationForecast:
                    if (pick.id.toString().includes('cfc')) {
                        return BetType.CombinationForecast;
                    }

                    return BetType.Forecast;
                case ParticipantPickType.Tricast:
                    return BetType.Tricast;
                case ParticipantPickType.CombinationTricast:
                    if (pick.id.toString().includes('ctc')) {
                        return BetType.CombinationTricast;
                    }

                    return BetType.Tricast;

                default:
                    return BetType.None;
            }
        }
    }

    private generatePicks(slipPick: ISlipPick): Pick[] {
        const picks: Pick[] = [];
        if (slipPick.pick instanceof BetslipV2OptionMarketXCastRacePick) {
            const participantPick = slipPick.pick;

            participantPick.options.forEach((option, index) => {
                const pick: Pick = {
                    id: option.prices[0].id.toString(10),
                    optionIdReference: option.id.toString(),
                    pickDetails: {
                        position: option.position!,
                        participantName: option.name.name,
                        altParticipantName: option.name.nameAlternate || EmptyName,
                        priceType: this.getPriceType(slipPick.price.type),
                        drawNumber: (0).toString(),
                    },
                };

                picks.push(pick);
            });
        }
        if (slipPick.pick instanceof BetslipV2HorseRaceOptionMarketPick) {
            const option = slipPick.pick.option;

            const pick: Pick = {
                id: option.prices[0].id.toString(10),
                optionIdReference: option.id.toString(),
                pickDetails: {
                    position: option.position!,
                    participantName: option.name.name,
                    altParticipantName: option.name.nameAlternate || EmptyName,
                    priceType: this.getPriceType(slipPick.price.type),
                    drawNumber: (0).toString(),
                },
            };

            picks.push(pick);
        }

        return picks;
    }

    private getPriceType(priceType: PriceType): BposPriceType {
        switch (priceType) {
            case PriceType.Fixed:
                return BposPriceType.Fixed;
            case PriceType.NoPrice:
                return BposPriceType.NoPrice;
            case PriceType.StartingPrice:
                return BposPriceType.StartingPrice;
            default:
                return BposPriceType.None;
        }
    }
}
